import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { DropdownButton, Dropdown, Col } from "react-bootstrap";
import { Box, ButtonStyled, SpanStyled } from "../styled/Elements";
import skyImage from "../../assets/images/png/sky_pattern.png";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, WhiteSearchIcon } from "../styled/AllImages";
import { useNavigate } from "react-router-dom";
import { listingTypes, roles, statusTypes } from "../data/data";
import { useUser } from "../../context/userContext";
import PropertiesApi from "../../services/api/propertiesApi";
import VisitorApi from "../../services/api/visitorApi";
import GeneralRequestCta from "../common/GeneralRequestCta";

const NewBanner = () => {
  const { setSearchProfileData, setTotalPages, setCurrentPage, setPerPage } =
    useUser();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [cityData, setCityData] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedListingType, setSelectedListingType] = useState(null);
  const [selectedPropertyType, setSelectedPropertyType] = useState(null);
  const [selectedPropertyStatus, setSelectedPropertyStatus] = useState(null);

  const [propertyTypes, setPropertyTypes] = useState(null);

  const [showAdvanceSearch, setShowAdvanceSearch] = useState(false);
  const [btnWidth, setBtnWidth] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [districtSearch, setDistrictSearch] = useState("");
  const [errors, setErrors] = useState({});
  const isCityAll = selectedCity?.value === "all";
  const cityTitle = isCityAll
    ? t("all")
    : cityData?.find((city) => parseInt(selectedCity?.value) === city?.id)
        ?.label;
  const cityId = isCityAll
    ? null
    : cityData?.find((city) => parseInt(selectedCity?.value) === city?.id)
        ?.value;
  const districtTitle = districts?.find(
    (district) => parseInt(selectedDistrict?.value) === district?.id
  )?.label;
  const propertyTypeTitle = propertyTypes?.find(
    (type) => parseInt(selectedPropertyType?.value) === parseInt(type?.id)
  )?.label;

  // const [searchProfileData, setSearchProfileData] = useState([]);

  const isRTL = i18n.language === "ar";

  const filteredCities = cityData.filter((city) =>
    city?.label?.toLowerCase().includes(citySearch.toLowerCase())
  );

  const filteredDistricts = districts.filter((dst) =>
    dst?.label?.toLowerCase().includes(districtSearch.toLowerCase())
  );

  const handleResetSearch = () => {
    setSelectedCity(null);
    setSelectedRole(null);
    setSelectedDistrict(null);
    setSelectedListingType(null);
    setSelectedPropertyType(null);
    setSelectedPropertyStatus(null);
    setShowAdvanceSearch(false);
  };

  const searchProfile = async () => {
    const newErrors = {};

    if (!selectedRole) {
      newErrors.role = t("field_required");
    }

    if (!selectedCity) {
      newErrors.city = t("field_required");
    }

    // if (showAdvanceSearch) {
    //   if (!selectedListingType) {
    //     newErrors.listing_type_id = t("field_required");
    //   }
    //   if (!selectedPropertyType) {
    //     newErrors.listing_property_type_id = t("field_required");
    //   }
    //   if (!selectedPropertyStatus) {
    //     newErrors.property_status = t("field_required");
    //   }
    // }

    setErrors(newErrors);

    // Prevent search if there are errors
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    try {
      const hasAdvanceFields = !!(
        selectedListingType?.id ||
        selectedPropertyType?.id ||
        selectedPropertyStatus?.id
      );
      const searchParams = {
        // Only advance when the advance UI is open and a field is set
        is_advance: showAdvanceSearch && hasAdvanceFields ? "1" : "0",
        include_incomplete: 1,
      };
      if (selectedRole?.id) {
        searchParams.professional_type = selectedRole.id;
      }
      // Omit city_id when "All" so every matching broker is returned
      if (selectedCity?.value && selectedCity.value !== "all") {
        searchParams.city_id = selectedCity.value;
      }
      if (showAdvanceSearch) {
        if (selectedListingType?.id) {
          searchParams.listing_type_id = selectedListingType.id;
        }
        if (selectedPropertyType?.id) {
          searchParams.listing_property_type_id = selectedPropertyType.id;
        }
        if (selectedPropertyStatus?.id) {
          searchParams.property_status = selectedPropertyStatus.id;
        }
      }
      const body = await VisitorApi.searchProfile(searchParams);
      if (body) {
        setCurrentPage(body?.meta?.current_page);
        setPerPage(body?.meta?.per_page);
        setTotalPages(body?.meta?.total);
        const formattedData = body?.data?.data?.map((item) => ({
          ...item,
          city: item?.personal_information?.city_name,
        }));
        setSearchProfileData(formattedData);

        const queryParams = new URLSearchParams();

        if (selectedRole?.id) queryParams.append("role", selectedRole.id);
        if (selectedCity?.value === "all") {
          queryParams.append("city", "all");
        } else if (selectedCity?.value) {
          queryParams.append("city", selectedCity.value);
        }

        if (showAdvanceSearch) {
          if (selectedListingType?.id)
            queryParams.append("listing_type", selectedListingType.id);
          if (selectedPropertyType?.value)
            queryParams.append("property_type", selectedPropertyType.value);
          if (selectedPropertyStatus?.id)
            queryParams.append("property_status", selectedPropertyStatus.id);
          queryParams.append("advance", true);
        }
        navigate(`/search-profiles?${queryParams.toString()}`, {
          state: {
            role: selectedRole,
            city: selectedCity,
            district: selectedDistrict,
            listingType: selectedListingType,
            propertyType: selectedPropertyType,
            propertyStatus: selectedPropertyStatus,
          },
        });
      }
    } catch (error) {
      return error;
    }
  };

  const getCityData = async () => {
    try {
      const body = await VisitorApi.getCitiesList();
      if (body?.data) {
        setCityData(
          body?.data?.map((city) => ({
            ...city,
            value: city?.id,
            label: isRTL ? city?.name_ar : city?.name_en,
          }))
        );
      }
    } catch (error) {}
  };

  const getDistrict = async (cityId) => {
    try {
      const body = await VisitorApi.getDistricts(cityId);
      const data = body?.data;

      if (Array.isArray(data) && data.length > 0) {
        setDistricts(
          data.map((dst) => ({
            ...dst,
            value: dst.id,
            label: isRTL ? dst.name_ar : dst.name_en,
          }))
        );
      } else {
        setDistricts([]);
      }
    } catch (error) {
      setDistricts([]);
    }
  };
  const getPropertyTypes = async () => {
    try {
      const response = await PropertiesApi.getListingPropertyTypes();
      if (response?.data) {
        setPropertyTypes(
          response?.data &&
            response?.data
              ?.filter((item) => item?.id != 15)
              ?.map((item) => {
                return {
                  ...item,
                  label: isRTL ? item?.ar_name : item?.en_name,
                  value: item?.id,
                };
              })
        );
      }
    } catch (error) {
      console.log(error.message);
      setPropertyTypes([]);
    }
  };

  useEffect(() => {
    getCityData();
    getPropertyTypes();
  }, [i18n.language]);

  useEffect(() => {
    if (cityId) {
      getDistrict(cityId);
    } else {
      setDistricts([]);
    }
  }, [cityId, i18n.language]);

  useEffect(() => {
    const handleResize = () => {
      const sWidth = window.innerWidth;
      if (sWidth <= 627) {
        setBtnWidth("100%");
      } else {
        setBtnWidth("");
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <BannerWrapper id="home">
      <BannerContainer>
        <Title lang={i18n.language}>{t("new_banner_title")}</Title>
        <Description>{t("new_banner_description")}</Description>

        <SearchAndCtaRow>
          <SearchBarWrapper>
            <SearchBar>
              <Col>
                <Box className="d-flex align-items-center">
                  <InputGroup lang={i18n.language}>
                    <Label lang={i18n.language}>
                      {t("looking_for")}{" "}
                      <span style={{ color: "var(--themeColorSecondary)" }}>
                        *
                      </span>
                    </Label>
                    <SubLabel lang={i18n.language}>
                      {t(selectedRole?.role) || t("select_professional_type")}
                    </SubLabel>
                    <StyledDropdownButton
                      lang={i18n.language}
                      id="property-type"
                      className="property-type"
                    >
                      {roles.map((role) => (
                        <Dropdown.Item
                          onClick={() => setSelectedRole(role)}
                          key={role?.id}
                        >
                          {t(role?.role)}
                        </Dropdown.Item>
                      ))}
                    </StyledDropdownButton>
                    {errors.role && <ErrorText>{errors.role}</ErrorText>}
                  </InputGroup>

                  <InputGroup lang={i18n.language}>
                    <Label lang={i18n.language}>
                      {t("city")}{" "}
                      <span style={{ color: "var(--themeColorSecondary)" }}>
                        *
                      </span>
                    </Label>
                    <SubLabel lang={i18n.language}>
                      {cityTitle ? cityTitle : t("select_city")}
                    </SubLabel>
                    <StyledDropdownButton
                      lang={i18n.language}
                      className="property-type"
                      id="city"
                      title={selectedCity?.label || t("select_city")}
                    >
                      <div className="dropdown-search-wrapper">
                        <input
                          type="text"
                          style={{
                            color: "var(--text)",
                            borderRadius: "8px",
                            border: "0.5px solid lightgray",
                            padding: "2px 5px",
                            width: "100%",
                          }}
                          placeholder={t("search")}
                          value={citySearch}
                          onChange={(e) => setCitySearch(e.target.value)}
                          className="dropdown-search-input"
                        />
                      </div>
                      <Dropdown.Item
                        key="all"
                        onClick={() => {
                          setSelectedCity({
                            id: "all",
                            value: "all",
                            label: t("all"),
                          });
                          setSelectedDistrict(null);
                          setDistricts([]);
                          setCitySearch("");
                        }}
                      >
                        {t("all")}
                      </Dropdown.Item>
                      {filteredCities.length > 0 ? (
                        filteredCities.map((city) => (
                          <Dropdown.Item
                            key={city.id}
                            onClick={() => {
                              setSelectedCity(city);
                              setSelectedDistrict(null);
                              setDistricts([]);
                              getDistrict(city.value);
                              setCitySearch("");
                            }}
                          >
                            {city.label}
                          </Dropdown.Item>
                        ))
                      ) : (
                        <Dropdown.Item disabled>{t("no_data")}</Dropdown.Item>
                      )}
                    </StyledDropdownButton>
                    {errors.city && <ErrorText>{errors.city}</ErrorText>}
                  </InputGroup>

                  <InputGroup lang={i18n.language}>
                    <Label lang={i18n.language}>{t("within")}</Label>
                    <SubLabel lang={i18n.language}>
                      {districtTitle
                        ? districtTitle
                        : selectedDistrict?.label
                        ? selectedDistrict?.label
                        : isCityAll
                        ? t("select_neighborhood")
                        : cityTitle
                        ? cityTitle
                        : t("select_neighborhood")}
                    </SubLabel>
                    <StyledDropdownButton
                      lang={i18n.language}
                      disabled={!selectedCity || isCityAll}
                      style={{
                        pointerEvents:
                          !selectedCity || isCityAll ? "none" : "auto",
                        opacity: !selectedCity || isCityAll ? 0.6 : 1,
                      }}
                      className="property-type"
                      id="neighborhood"
                      title={selectedDistrict?.label || t("select_neighborhood")}
                    >
                    <div className="dropdown-search-wrapper">
                      <input
                        type="text"
                        style={{
                          color: "var(--text)",
                          borderRadius: "8px",
                          border: "0.5px solid lightgray",
                          padding: "2px 5px",
                          width: "100%",
                        }}
                        placeholder={t("search")}
                        value={districtSearch}
                        onChange={(e) => setDistrictSearch(e.target.value)}
                        className="dropdown-search-input"
                      />
                    </div>
                    {filteredDistricts.length > 0 ? (
                      filteredDistricts.map((dst) => (
                        <Dropdown.Item
                          key={dst.id}
                          onClick={() => {
                            setSelectedDistrict(dst);
                            setDistrictSearch("");
                          }}
                        >
                          {dst.label}
                        </Dropdown.Item>
                      ))
                    ) : (
                      <Dropdown.Item disabled>
                        {t("no_neighborhood")}
                      </Dropdown.Item>
                    )}
                  </StyledDropdownButton>
                </InputGroup>
              </Box>
              {showAdvanceSearch && (
                <Box className="d-flex align-items-center mt-3">
                  <InputGroup lang={i18n.language}>
                    <Label lang={i18n.language}>{t("listing_type")} </Label>
                    <SubLabel lang={i18n.language}>
                      {t(selectedListingType?.label) ||
                        t("select_listing_type")}
                    </SubLabel>
                    <StyledDropdownButton
                      lang={i18n.language}
                      id="property-type"
                      className="property-type"
                    >
                      {listingTypes?.map((type) => (
                        <Dropdown.Item
                          onClick={() => setSelectedListingType(type)}
                          key={type?.id}
                        >
                          {t(type?.label)}
                        </Dropdown.Item>
                      ))}
                      {/* <Dropdown.Item>Villa</Dropdown.Item>
                <Dropdown.Item>Office</Dropdown.Item> */}
                    </StyledDropdownButton>
                    {errors.listing_type_id && (
                      <ErrorText>{errors.listing_type_id}</ErrorText>
                    )}
                  </InputGroup>

                  <InputGroup lang={i18n.language}>
                    <Label lang={i18n.language}>{t("property_type")} </Label>
                    <SubLabel lang={i18n.language}>
                      {propertyTypeTitle || t("select_property_type")}
                    </SubLabel>
                    <StyledDropdownButton
                      lang={i18n.language}
                      className="property-type"
                      id="property-type"
                      title={propertyTypeTitle || t("select_property_type")}
                    >
                      {propertyTypes?.length > 0 ? (
                        propertyTypes.map((type) => (
                          <Dropdown.Item
                            key={type.id}
                            onClick={() => {
                              setSelectedPropertyType(type);
                            }}
                          >
                            {type.label}
                          </Dropdown.Item>
                        ))
                      ) : (
                        <Dropdown.Item disabled>{t("no_data")}</Dropdown.Item>
                      )}
                    </StyledDropdownButton>
                    {errors.listing_property_type_id && (
                      <ErrorText>{errors.listing_property_type_id}</ErrorText>
                    )}
                  </InputGroup>

                  <InputGroup lang={i18n.language}>
                    <Label lang={i18n.language}>{t("property_status")}</Label>
                    <SubLabel lang={i18n.language}>
                      {t(selectedPropertyStatus?.label) ||
                        t("select_property_status")}
                    </SubLabel>
                    <StyledDropdownButton
                      lang={i18n.language}
                      className="property-type"
                      id="property-type"
                      title={
                        t(selectedPropertyStatus?.label) ||
                        t("select_property_status")
                      }
                    >
                      {statusTypes.length > 0 ? (
                        statusTypes.map((type) => (
                          <Dropdown.Item
                            key={type.id}
                            onClick={() => {
                              setSelectedPropertyStatus(type);
                            }}
                          >
                            {t(type.label)}
                          </Dropdown.Item>
                        ))
                      ) : (
                        <Dropdown.Item disabled>{t("no_data")}</Dropdown.Item>
                      )}
                    </StyledDropdownButton>
                  </InputGroup>
                </Box>
              )}
              <Box
                style={{
                  textAlign: "start",
                  cursor: "pointer",
                  width: "fit-content",
                }}
                className="d-flex align-items-center mx-2 mt-2 gap-1 advance-btn"
                onClick={() => setShowAdvanceSearch(!showAdvanceSearch)}
              >
                <SpanStyled size="14px" color="var(--themeColorSecondary)">
                  {t("advance_search")}
                </SpanStyled>{" "}
                {showAdvanceSearch ? (
                  <ArrowDown width="18px" />
                ) : (
                  <ArrowUp width="18px" />
                )}
              </Box>
            </Col>

            <SearchButtonWrapper>
              <Box>
                <ButtonStyled
                  width={btnWidth}
                  icon={WhiteSearchIcon}
                  label={t("search_property")}
                  color="var(--white)"
                  bg="var(--themeColorSecondary)"
                  className={""}
                  // clName={"signup-btn"}
                  size="16px"
                  padding="11px 20px"
                  onClick={() => {
                    searchProfile();
                  }}
                />
                <Box className="mt-2">
                  <SpanStyled
                    size="14px"
                    color="var(--themeColorSecondary)"
                    style={{ cursor: "pointer" }}
                    onClick={handleResetSearch}
                  >
                    {t("reset_search")}
                  </SpanStyled>
                </Box>
              </Box>
            </SearchButtonWrapper>
          </SearchBar>
          </SearchBarWrapper>
          <GeneralRequestCta variant="inline" />
        </SearchAndCtaRow>
      </BannerContainer>
    </BannerWrapper>
  );
};

export default NewBanner;

export const BannerWrapper = styled.section`
  min-height: 470px;
  overflow: visible;
  height: fit-content;
  padding: 0 12px;
  padding-top: 7rem;
  background-repeat: no-repeat;
  width: 100%;
  /* overflow: hidden; */
  background-image: url(${skyImage});
  background-repeat: no-repeat;
  background-size: cover;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.5);
    z-index: 1;
  }
  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), #ffffff);
    z-index: 1;
  }

  > * {
    position: relative;
    z-index: 2;
  }

  .advance-btn {
    svg {
      [stroke] {
        stroke: var(--themeColorSecondary);
      }
    }
  }
  /* @media (max-width: 991px) {
    min-height: 100%;
    height: 100%;
  } */
`;
const BannerContainer = styled.section`
  padding: 60px 10px;
  max-width: 1180px;
  /* overflow-x: hidden; */
  text-align: center;
  border-radius: 20px;
  background: #ffffffb1;
  margin-left: auto;
  margin-right: auto;
  /* box-shadow: inset -18px 0 15px rgba(0, 0, 0, 0.1); */
  z-index: 3;
  border: 1px solid #fff;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 10px;
  color: var(--themeColorSecondary);
  font-family: ${({ lang }) =>
    lang === "en" ? "var(--heavy)" : "var(--arExtraBold)"};
  /* @media (max-width: 482px) {
    font-size: 1.6rem;
  } */
`;

const Description = styled.p`
  font-size: 1.1rem;
  color: var(--themeColor);
  margin-bottom: 30px;
  /* @media (max-width: 482px) {
    font-size: 0.8rem;
  } */
`;

const SearchAndCtaRow = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 14px;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchBarWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 855px;
  margin: 0;
`;

const SearchBar = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  background-color: transparent;
  border-radius: 16px;
  padding: 12px;
  position: relative;
  z-index: 1;
  gap: 12px;
  border: 1px solid transparent;
  background: linear-gradient(white, white) padding-box,
    linear-gradient(to right, #6aeef0, #ffffff 40%, #ffffff 60%, #acceaa)
      border-box;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);

  @media (max-width: 627px) {
    align-items: stretch;
  }
  @media (max-width: 425px) {
    padding: 12px 6px;
  }
`;

const InputGroup = styled.div`
  flex: 1;
  /* height: 100%; */
  position: relative;
  padding: 0 12px;
  text-align: left;
  .property-type {
    position: absolute;
    height: 100%;
    right: 3%;
    bottom: 4%;
    background: transparent !important;
  }

  &:not(:first-child) {
    border-left: ${({ lang }) => (lang === "ar" ? "none" : "1px solid #ccc")};
    border-right: ${({ lang }) => (lang === "en" ? "none" : "1px solid #ccc")};
  }

  @media (max-width: 425px) {
    padding: 0 5px;
  }
`;

const Label = styled.div`
  font-weight: 600;
  width: 100%;
  white-space: nowrap;
  font-size: 0.9rem;
  color: var(--text);
  text-align: ${({ lang }) => (lang === "en" ? "left" : "right")};
  @media (max-width: 482px) {
    font-size: 0.75rem;
  }
  @media (max-width: 415px) {
    font-size: 0.9rem;
  }
`;

const SubLabel = styled.div`
  font-size: 0.75rem;
  width: 100%;
  white-space: nowrap;
  color: var(--lightGrey);
  text-align: ${({ lang }) => (lang === "en" ? "left" : "right")};
  @media (max-width: 482px) {
    font-size: 0.6rem;
  }
`;

const StyledDropdownButton = styled(DropdownButton)`
  width: 100%;
  border: none !important;
  background: white !important;
  padding: 0 !important;
  color: #333;
  border-radius: 0;
  box-shadow: none;
  font-size: 1rem;
  margin-top: 4px;
  height: 100%;

  .dropdown-search-wrapper {
    width: 100%;
    position: sticky;
    top: 0;
    display: flex;
    justify-content: center;
    padding: 5px 12px;
  }

  &::after {
    display: none !important;
  }

  &:focus,
  &:active {
    box-shadow: none !important;
    outline: none !important;
  }

  .dropdown-toggle {
    background-color: transparent !important;
    border: none !important;
    padding: 0;
    height: 100%;
    width: 100%;
    color: transparent !important;
  }

  .dropdown-menu {
    width: 100%;
    max-height: 169px;
    height: fit-content;
    overflow-y: auto;
    overflow-x: hidden;
    border-radius: 12px !important;
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
    margin-top: 12px;
    border: none !important;
    z-index: 1050 !important;

    direction: ${({ lang }) => (lang === "ar" ? "rtl" : "ltr")};
    text-align: ${({ lang }) =>
      lang === "ar" ? "right !important" : "left !important"};

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #ccc;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }

  .dropdown-item {
    padding: 10px 15px;
    font-size: 0.95rem;
    color: #333;
    background-color: white !important;
    text-align: ${({ lang }) => (lang === "ar" ? "right" : "left")};
  }

  .dropdown-item:hover,
  .dropdown-item:focus {
    background-color: #f1f1f1 !important;
    color: #000 !important;
  }
`;

const SearchButton = styled.button`
  background-color: #28a745;
  color: white;
  border: none;
  padding: 12px 22px;
  display: flex;
  align-items: center;
  border-radius: 6px;
  cursor: pointer;
  margin-left: 12px;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #218838;
  }

  svg {
    font-size: 20px;
  }
`;

const SearchButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 8px 12px;

  @media (max-width: 627px) {
    justify-content: center;
    width: 100%;
  }
`;

export const SearchInputWrapper = styled.div`
  width: 100%;
  margin-bottom: 8px;
  input {
    width: 100%;
    padding: 10px 16px;
    border: 1px solid #ccc;
    border-radius: 10px;
    font-size: 1rem;
    outline: none;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  }
`;

const ErrorText = styled.div`
  color: #cc2f2f;
  font-size: 12px;
  margin-top: 4px;
`;
